import { HTMLInputTypeAttribute, useEffect, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  TValidationMethod,
  getIsFieldInvalid,
} from '../utils/forms/forms.utils';

interface Props {
  name: string;
  type?: HTMLInputTypeAttribute;
  validationMethod?: TValidationMethod;
  isFocused?: boolean;
  maxLength?: number;
  showMaxLengthCounter?: boolean;
  showValidationError?: boolean;
}

function useFormField<FormValueType = any, FormValuesType = unknown>({
  name,
  type,
  validationMethod,
  isFocused,
  maxLength,
  showMaxLengthCounter,
  showValidationError,
}: Props) {
  const [field, meta, helpers] = useField<FormValueType>({
    name,
    type: type ?? 'text',
  });
  const formikContext = useFormikContext<FormValuesType>();
  const [isTyped, setIsTyped] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isInvalid, setIsInvalid] = useState(false);
  const [prevSubmitCount, setPrevSubmitCount] = useState(
    formikContext.submitCount
  );

  useEffect(() => {
    if (
      validationMethod !== 'on-submit-always' &&
      validationMethod !== 'on-submit'
    )
      return;

    // Only mark as 'submitted' in case difference is exactly 1.
    // Other cases means that field was hidden and should not be validated on appearence
    if (formikContext.submitCount - prevSubmitCount === 1) {
      setIsSubmitted(true);
    }

    setPrevSubmitCount(formikContext.submitCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikContext.submitCount, validationMethod]);

  // In on-submit validation mode -
  // disable validation after value is unset
  useEffect(() => {
    if (validationMethod !== 'on-submit') return;

    if (!field.value && meta.touched) {
      formikContext.setFieldError(field.name, undefined);
      setIsSubmitted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationMethod, field.value, field.name]);

  useEffect(() => {
    const isInvalidRes = validationMethod
      ? getIsFieldInvalid({
          validationMethod,
          meta,
          isTyped,
          isSubmitted,
        })
      : false;

    setIsInvalid(isInvalidRes);
  }, [validationMethod, meta, isTyped, isSubmitted]);

  const { t } = useTranslation();

  const shouldShowCounter = !!(
    isFocused &&
    maxLength &&
    showMaxLengthCounter &&
    !isInvalid
  );

  const getErrorMessage = () => {
    if (!showValidationError || !isInvalid) return;
    if (meta.error) {
      return t(meta.error!);
    }
  };

  const errorMessage = getErrorMessage();

  return {
    field,
    meta,
    helpers,
    formikContext,
    isTyped,
    isInvalid,
    t,
    setIsTyped,
    shouldShowCounter,
    errorMessage,
  };
}

export { useFormField };
